/**
 * FleetOrdersBottomSheet Component - Web Version
 *
 * Draggable bottom sheet displaying fleet orders list
 * Overlays the map on FleetHub dashboard
 *
 * @module components/FleetOrdersBottomSheet
 */

import { FC, useState, useCallback, useEffect } from "react"
import {
  View,
  ViewStyle,
  TextStyle,
  TouchableOpacity,
  ScrollView,
  Dimensions,
  RefreshControl,
  useWindowDimensions,
} from "react-native"
import Animated, {
  FadeIn,
  FadeOut,
  useSharedValue,
  useAnimatedStyle,
  withTiming,
} from "react-native-reanimated"
import Icon from "react-native-vector-icons/MaterialIcons"

import { Button } from "@/components/Button"
import { FilterBar } from "@/components/FilterBar.web"
import { FleetOrderGhostCard } from "@/components/FleetOrderGhostCard"
import { FleetOrderRow } from "@/components/FleetOrderRow"
import { FleetOrderRowSkeleton } from "@/components/FleetOrderRowSkeleton"
import {
  $bottomSheet,
  $dragHandle,
  $pillHandleContainer,
  $pillHandle,
  $pillHandleHovered,
  $dragHandleContent,
  $headerTitle,
  $headerTitleMobile,
  $content,
  $listContent,
  $emptyState,
  $emptyStateTitle,
  $emptyStateText,
} from "@/components/fleetOrdersSheet.styles"
import { NumberPlateSearchBar } from "@/components/NumberPlateSearchBar"
import { Pagination } from "@/components/Pagination"
import { Text } from "@/components/Text"
import {
  useFleetHubStore,
  selectTotalPages,
  selectHasOrders,
  selectHasActiveDateFilter,
  selectIsSearchActive,
  type FleetOrder,
} from "@/stores/useFleetHubStore"
import { colors, spacing } from "@/theme"

export interface FleetOrdersBottomSheetProps {
  /**
   * Handlers for order actions
   */
  onDownloadPdf: (order: FleetOrder) => void
  onOpenTracking: (order: FleetOrder) => void
  onReschedule: (order: FleetOrder) => void
  onCancel: (order: FleetOrder) => void
  /**
   * Callback when bottom sheet height changes (for map bounds calculation)
   */
  onHeightChange?: (height: number) => void
}

const { height: SCREEN_HEIGHT } = Dimensions.get("window")

export const FleetOrdersBottomSheet: FC<FleetOrdersBottomSheetProps> = ({
  onDownloadPdf,
  onOpenTracking,
  onReschedule,
  onCancel,
  onHeightChange,
}) => {
  // Zustand store hooks
  const orders = useFleetHubStore((state) => state.orders)
  const isLoading = useFleetHubStore((state) => state.isLoading)
  const error = useFleetHubStore((state) => state.error)
  const currentPage = useFleetHubStore((state) => state.currentPage)
  const loadOrders = useFleetHubStore((state) => state.loadOrders)
  const setPage = useFleetHubStore((state) => state.setPage)
  const showLoadingCard = useFleetHubStore((state) => state.showLoadingCard)
  const pendingOrderDraft = useFleetHubStore((state) => state.pendingOrderDraft)
  const loadingCardPhase = useFleetHubStore((state) => state.loadingCardPhase)
  const pendingRowAction = useFleetHubStore((state) => state.pendingRowAction)
  const openBookingModal = useFleetHubStore((state) => state.openBookingModal)
  const openBookingModalWithNachkontrolle = useFleetHubStore(
    (state) => state.openBookingModalWithNachkontrolle,
  )
  const hideLoadingCard = useFleetHubStore((state) => state.hideLoadingCard)
  const highlightedOrderId = useFleetHubStore((state) => state.highlightedOrderId)

  // Filter hooks
  const filters = useFleetHubStore((state) => state.filters)
  const setFilters = useFleetHubStore((state) => state.setFilters)
  const applyFilters = useFleetHubStore((state) => state.applyFilters)

  // Search hooks
  const searchByPlate = useFleetHubStore((state) => state.searchByPlate)
  const clearSearch = useFleetHubStore((state) => state.clearSearch)
  const isSearchActive = useFleetHubStore(selectIsSearchActive)

  // Computed values
  const totalPages = useFleetHubStore(selectTotalPages)
  const hasOrders = useFleetHubStore(selectHasOrders)
  const hasActiveDateFilter = useFleetHubStore(selectHasActiveDateFilter)

  // Responsive breakpoints
  const { width } = useWindowDimensions()
  const isSmallMobile = width < 360 // Very small screens (iPhone SE, etc)
  const isMobile = width < 768 // Mobile devices

  // UI state
  const [isExpanded, setIsExpanded] = useState(false)
  const [windowHeight, setWindowHeight] = useState(SCREEN_HEIGHT)
  const [isDragHandleHovered, setIsDragHandleHovered] = useState(false)

  // Animation for the handle arrow
  const translateY = useSharedValue(0)

  // Calculate heights
  const minHeight = windowHeight * 0.5 // 50% of screen
  const maxHeight = windowHeight * 0.75 // 75% of screen
  const currentHeight = isExpanded ? maxHeight : minHeight

  // Handle window resize
  useEffect(() => {
    const handleResize = () => {
      setWindowHeight(Dimensions.get("window").height)
    }

    const subscription = Dimensions.addEventListener("change", handleResize)

    return () => {
      subscription?.remove()
    }
  }, [])

  // Arrow lift animation on hover
  useEffect(() => {
    if (isDragHandleHovered && !isExpanded) {
      // Pull up when hovering
      translateY.value = withTiming(-5, { duration: 200 })
    } else {
      // Return to normal position
      translateY.value = withTiming(0, { duration: 200 })
    }
  }, [isDragHandleHovered, isExpanded, translateY])

  // Animated style for arrow
  const _animatedArrowStyle = useAnimatedStyle(() => {
    return {
      transform: [{ translateY: translateY.value }],
    }
  })

  // Report height changes to parent for map bounds calculation
  useEffect(() => {
    if (onHeightChange) {
      // Report current height whenever it changes
      onHeightChange(currentHeight)
    }
  }, [currentHeight, onHeightChange])

  const toggleExpanded = useCallback(() => {
    setIsExpanded(!isExpanded)
  }, [isExpanded])

  /**
   * Handle sort direction change (applies immediately)
   */
  const handleSortDirectionChange = useCallback(
    async (direction: "asc" | "desc") => {
      console.log("[FleetOrdersBottomSheet] Changing sort direction:", direction)
      setFilters({ ...filters, sortDirection: direction })
      await applyFilters()
    },
    [filters, setFilters, applyFilters],
  )

  /**
   * Handle date range change (applies when modal is closed)
   */
  const handleDateRangeChange = useCallback(
    async (dateRange: typeof filters.dateRange) => {
      console.log("[FleetOrdersBottomSheet] Applying date range:", dateRange)
      setFilters({ ...filters, dateRange })
      await applyFilters()
    },
    [filters, setFilters, applyFilters],
  )

  /**
   * Handle clear date filter
   */
  const handleClearDateFilter = useCallback(async () => {
    console.log("[FleetOrdersBottomSheet] Clearing date filter")
    setFilters({ ...filters, dateRange: { startDate: null, endDate: null } })
    await applyFilters()
  }, [filters, setFilters, applyFilters])

  /**
   * Render empty state
   */
  const renderEmptyState = () => (
    <View style={$emptyState}>
      <Icon name="inbox" size={48} color={colors.palette.neutral350} />
      <Text style={$emptyStateTitle}>Keine Aufträge gefunden</Text>
      <Text style={$emptyStateText}>
        Es wurden noch keine Aufträge für Ihr Unternehmen erfasst.
      </Text>
    </View>
  )

  /**
   * Render error state
   */
  const renderErrorState = () => (
    <View style={$errorState}>
      <Icon name="error-outline" size={48} color={colors.palette.neutral350} />
      <Text style={$errorStateTitle}>Fehler beim Laden</Text>
      <Text style={$errorStateText}>{error}</Text>
      <Button
        text="Erneut versuchen"
        onPress={() => loadOrders(currentPage)}
        preset="filled"
        style={$retryButton}
      />
    </View>
  )

  /**
   * Render loading state with skeleton cards
   */
  const renderLoadingState = () => (
    <View style={$skeletonContainer}>
      {Array.from({ length: 3 }).map((_, index) => (
        <FleetOrderRowSkeleton key={index} />
      ))}
    </View>
  )

  return (
    <Animated.View
      entering={FadeIn.duration(200)}
      exiting={FadeOut.duration(200)}
      // @ts-ignore - Web-specific data attribute for map bounds calculation
      data-bottom-sheet="true"
      style={[
        $bottomSheet,
        {
          height: currentHeight,
          maxHeight: maxHeight,
        },
      ]}
    >
      {/* Drag Handle with Title */}
      <TouchableOpacity
        style={[$dragHandle, isDragHandleHovered && $dragHandleHovered]}
        onPress={toggleExpanded}
        activeOpacity={0.7}
        // @ts-ignore - Web-specific hover events
        onMouseEnter={() => setIsDragHandleHovered(true)}
        onMouseLeave={() => setIsDragHandleHovered(false)}
        nativeID="tutorial-drag-handle"
      >
        {/* Pill handle */}
        <View style={$pillHandleContainer}>
          <View style={[$pillHandle, isDragHandleHovered && $pillHandleHovered]} />
        </View>

        {/* Title */}
        <View style={$dragHandleContent}>
          <Text style={isMobile ? $headerTitleMobile : $headerTitle} preset="heading">
            Meine Aufträge
          </Text>
        </View>
      </TouchableOpacity>

      {/* Filter Bar with Booking Button */}
      <FilterBar
        filters={filters}
        onSortDirectionChange={handleSortDirectionChange}
        onDateRangeChange={handleDateRangeChange}
        onClearDateFilter={handleClearDateFilter}
        hasActiveDateFilter={hasActiveDateFilter}
        isSearchActive={isSearchActive}
        searchPlate={
          <NumberPlateSearchBar
            onSearch={searchByPlate}
            onClear={clearSearch}
            isSearchActive={isSearchActive}
            isLoading={isLoading}
            inline
          />
        }
        bookingButton={
          <View nativeID="tutorial-booking-btn">
            <Button
              text={isMobile ? "Neu buchen" : "Neue Inspektion buchen"}
              onPress={openBookingModal}
              preset="filled"
              style={[
                $bookingButton,
                isSmallMobile && $bookingButtonSmall,
                isMobile && $bookingButtonMobile,
              ]}
              textStyle={$bookingButtonText}
              LeftAccessory={() => (
                <Icon name="add" size={20} color={colors.surface} style={$buttonIcon} />
              )}
            />
          </View>
        }
      />

      {/* Content */}
      <View style={$content}>
        {isLoading && !hasOrders && !showLoadingCard ? (
          renderLoadingState()
        ) : error ? (
          renderErrorState()
        ) : !hasOrders && !showLoadingCard ? (
          renderEmptyState()
        ) : (
          <ScrollView
            contentContainerStyle={$listContent}
            showsVerticalScrollIndicator={true}
            refreshControl={
              <RefreshControl
                refreshing={isLoading}
                onRefresh={() => loadOrders(currentPage)}
                tintColor={colors.primary}
              />
            }
          >
            {/* Ghost card — real row layout with known booking draft + shimmer
                for server-assigned fields while HubSpot→Firestore sync completes */}
            {showLoadingCard && pendingOrderDraft && (
              <FleetOrderGhostCard
                draft={pendingOrderDraft}
                phase={loadingCardPhase}
                onDismiss={hideLoadingCard}
              />
            )}

            {/* Order List */}
            {orders.map((item) => (
              <FleetOrderRow
                key={String(item.id)}
                order={item}
                onDownloadPdf={onDownloadPdf}
                onOpenTracking={onOpenTracking}
                onReschedule={onReschedule}
                onCancel={onCancel}
                onBookNachkontrolle={openBookingModalWithNachkontrolle}
                onBookNewInspection={openBookingModal}
                isHighlighted={highlightedOrderId === item.id}
                pendingAction={pendingRowAction?.orderId === item.id ? pendingRowAction : undefined}
              />
            ))}

            {/* Pagination */}
            {totalPages > 1 && (
              <Pagination
                currentPage={currentPage}
                totalPages={totalPages}
                onPageChange={setPage}
              />
            )}
          </ScrollView>
        )}
      </View>
    </Animated.View>
  )
}

const $dragHandleHovered: ViewStyle = {
  // No background change — pill handle provides hover feedback
}

const $bookingButton: ViewStyle = {
  backgroundColor: colors.primary,
  paddingVertical: spacing.sm,
  paddingHorizontal: spacing.md,
  minHeight: 40,
}

// White label on the Ignition Red background. Button preset's default text
// color is Dusknight, which was unreadable on red. Hover state (gray) keeps
// the same white text — still readable.
const $bookingButtonText: TextStyle = {
  color: colors.surface,
}

const $bookingButtonMobile: ViewStyle = {
  paddingVertical: spacing.xs,
  paddingHorizontal: spacing.sm,
  minHeight: 36,
  flexShrink: 0, // Don't shrink button
}

const $bookingButtonSmall: ViewStyle = {
  maxWidth: "calc(100vw - 32px)" as any, // Prevent overflow on very small screens
  paddingHorizontal: spacing.sm, // Reduce padding to fit text
  minWidth: 0, // Remove minimum width constraint
  flexShrink: 1, // Allow button to shrink if needed
}

const $buttonIcon: ViewStyle = {
  marginRight: spacing.xxs,
}

const $errorState: ViewStyle = {
  flex: 1,
  justifyContent: "center",
  alignItems: "center",
  paddingHorizontal: spacing.xl,
}

const $errorStateTitle: TextStyle = {
  fontSize: 20,
  fontWeight: "600",
  color: colors.error,
  marginTop: spacing.md,
  marginBottom: spacing.sm,
}

const $errorStateText: TextStyle = {
  fontSize: 14,
  color: colors.textDim,
  textAlign: "center",
  marginBottom: spacing.lg,
}

const $retryButton: ViewStyle = {
  minWidth: 200,
  borderRadius: 20,
}

const $skeletonContainer: ViewStyle = {
  paddingHorizontal: spacing.xl,
  paddingVertical: spacing.lg,
}
